import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, Dimensions, ScrollView, Modal, Alert, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { fontFamily } from '../../helpers/fontFamily';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { fetchShopifyLocations, fetchShopifyProductInventory, fetchShopifyInventoryWithDefaultLocations, showInventoryReport, checkPushPermissions } from '../../actions/inventoryReportActions';

const pushInventory = ({ mode, onApplyPush, onReviewPush }) => {
  const [selectedOption, setSelectedOption] = useState('incoming');
  const [reportName, setReportName] = useState('');
  const [reportMode, setReportMode] = useState('receive');
  const [reportId, setReportId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [productsWithoutLocation, setProductsWithoutLocation] = useState([]);
  const [pendingInventoryData, setPendingInventoryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderTitle, setLoaderTitle] = useState('Loading...');
  const [pushPermissions, setPushPermissions] = useState({ can_push: false, has_mixed_stores: false, stores_with_push: [], stores_without_push: [] });
  const userHasManuallySelectedRef = useRef(false);
  const userHasManuallySelectedLocationRef = useRef(false);
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  const shopifyLocations = useSelector(state => state.inventoryReports.shopifyLocations);

  const isTablet = screenData.width >= 768;
  const isDesktop = screenData.width >= 1024;
  const isMobile = screenData.width < 768;

  useEffect(() => {
    const onChange = (result) => {
      setScreenData(result.window);
    };

    const subscription = Dimensions.addEventListener('change', onChange);

    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    const loadReportData = async () => {
      try {
        const [reportData, reportModeData, defaultQuantityType, defaultLocation] = await Promise.all([
          AsyncStorage.getItem('current_report'),
          AsyncStorage.getItem('current_report_mode'),
          AsyncStorage.getItem('default_inventory_quantity_type'),
          AsyncStorage.getItem('default_inventory_location')
        ]);
        const routeReportId = route.params?.reportId;

        if (reportData) {
          try {
            const report = JSON.parse(reportData);
            setReportName(report.name || '');
            setReportId(routeReportId || report.id || null);
          } catch (parseError) {
            console.error('Error parsing report data:', parseError);
            setReportName(reportData);
            setReportId(routeReportId || null);
          }
        } else {
          setReportName('');
          setReportId(routeReportId || null);
        }
        if (reportModeData) {
          setReportMode(reportModeData);
          if (reportModeData === 'recount') {
            setSelectedOption('available');
          } else if (!userHasManuallySelectedRef.current) {
            const cleanedQuantityType = defaultQuantityType?.toString().replace(/['"]/g, '').trim();

            if (cleanedQuantityType === 'available') {
              setSelectedOption('available');
            } else if (cleanedQuantityType === 'incoming') {
              setSelectedOption('incoming');
            } else {
              setSelectedOption('incoming');
            }
          }
        } else {
          setReportMode('receive');
          if (!userHasManuallySelectedRef.current) {
            const cleanedQuantityType = defaultQuantityType?.toString().replace(/['"]/g, '').trim();

            if (cleanedQuantityType === 'available') {
              setSelectedOption('available');
            } else if (cleanedQuantityType === 'incoming') {
              setSelectedOption('incoming');
            } else {
              setSelectedOption('incoming');
            }
          }
        }
      } catch (error) {
        setReportName('');
        setReportMode('receive');
        setReportId(route.params?.reportId || null);
        if (!userHasManuallySelectedRef.current) {
          setSelectedOption('incoming');
        }
      }
    };

    loadReportData();

    const checkPermissions = async () => {
      try {
        const permissions = await dispatch(checkPushPermissions(reportId));
        setPushPermissions(permissions);

      } catch (error) {
        setPushPermissions({ can_push: false, has_mixed_stores: false, stores_with_push: [], stores_without_push: [] });
      }
    };

    checkPermissions();

    if (reportId) {
      dispatch(fetchShopifyLocations(null, reportId));
    } else if (route.params?.storeIds?.length > 0) {
      dispatch(fetchShopifyLocations(route.params.storeIds));
    }
  }, [route.params?.storeIds, reportId, dispatch]);

  useEffect(() => {
    const selectDefaultLocation = async () => {
      if (Array.isArray(shopifyLocations) && shopifyLocations.length > 0 && !userHasManuallySelectedLocationRef.current) {
        try {
          const defaultLocation = await AsyncStorage.getItem('default_inventory_location');

          if (defaultLocation) {
            let cleanedDefaultLocation;
            try {
              cleanedDefaultLocation = JSON.parse(defaultLocation);
            } catch (e) {
              cleanedDefaultLocation = defaultLocation;
            }

            if (typeof cleanedDefaultLocation === 'string') {
              cleanedDefaultLocation = cleanedDefaultLocation.replace(/['"]/g, '').trim();
            }

            const matchingLocation = shopifyLocations.find(loc => {
              const nameMatch = loc.name === cleanedDefaultLocation;
              const address1Match = loc.address1 === cleanedDefaultLocation;
              const fullAddressMatch = `${loc.address1}${loc.address2 ? ' ' + loc.address2 : ''}` === cleanedDefaultLocation;

              return nameMatch || address1Match || fullAddressMatch;
            });

            if (matchingLocation) {
              setSelectedLocation(matchingLocation);
            } else {
              setSelectedLocation(shopifyLocations[0]);
            }
          } else {
            setSelectedLocation(shopifyLocations[0]);
          }
        } catch (error) {
          setSelectedLocation(shopifyLocations[0]);
        }
      }
    };

    selectDefaultLocation();
  }, [shopifyLocations]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    userHasManuallySelectedRef.current = true;
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    userHasManuallySelectedLocationRef.current = true;
  };

  const handleReviewAndPush = async () => {
    try {
      setIsLoading(true);
      setLoaderTitle('Fetching inventory data...');
      let productIds = [];
      let inventoryReportItems = [];

      if (reportId) {
        try {
          const response = await dispatch(showInventoryReport(reportId));

          if (response?.inventory_report_items && Array.isArray(response.inventory_report_items)) {
            inventoryReportItems = response.inventory_report_items;
            productIds = inventoryReportItems
              .map(item => item.product_id)
              .filter(id => id != null);
          }
        } catch (error) {
          console.error('Error fetching report data:', error);
        }
      }

      const inventoryData = await dispatch(fetchShopifyProductInventory(
        reportId,
        selectedLocation?.id || '',
        selectedLocation?.name || '',
        productIds,
        selectedOption
      ));

      if (inventoryData?.requires_confirmation && inventoryData?.products_without_location?.length > 0) {
        setProductsWithoutLocation(inventoryData.products_without_location);
        setPendingInventoryData(inventoryData);
        setShowConfirmationModal(true);
        return;
      }

      await AsyncStorage.removeItem('default_inventory_quantity_type');
      await AsyncStorage.removeItem('default_inventory_location');
      if (onReviewPush) {
        onReviewPush();
      } else {
        navigation.navigate('ViewInventoryReport', {
          id: reportId,
          view: 'show',
          mode: reportMode,
          from: 'pushInventory',
          selectedLocation: selectedLocation,
          inventoryData: inventoryData,
          selectedOption: selectedOption,
        });
      }
    } catch (error) {
      console.error('Error fetching product inventory:', error);

      if (onReviewPush) {
        onReviewPush();
      } else {
        navigation.navigate('ViewInventoryReport', {
          id: reportId,
          view: 'show',
          mode: reportMode,
          from: 'pushInventory',
          selectedLocation: selectedLocation,
          inventoryData: inventoryData,
          selectedOption: selectedOption,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmationCancel = () => {
    setShowConfirmationModal(false);
    setProductsWithoutLocation([]);
    setPendingInventoryData(null);
  };

  const handleConfirmationContinue = async () => {
    try {
      setShowConfirmationModal(false);
      setIsLoading(true);
      setLoaderTitle('Fetching inventory from default locations...');

      const inventoryData = await dispatch(fetchShopifyInventoryWithDefaultLocations(
        reportId,
        selectedLocation?.id || '',
        selectedLocation?.name || '',
        pendingInventoryData?.inventory_data?.map(item => item.product_id).join(',') || '',
        selectedOption
      ));

      await AsyncStorage.removeItem('default_inventory_quantity_type');
      await AsyncStorage.removeItem('default_inventory_location');

      if (onReviewPush) {
        onReviewPush();
      } else {
        navigation.navigate('ViewInventoryReport', {
          id: reportId,
          view: 'show',
          mode: reportMode,
          from: 'pushInventory',
          selectedLocation: selectedLocation,
          inventoryData: inventoryData,
          selectedOption: selectedOption,
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to fetch inventory data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <LinearGradient
        colors={['#0A1118', '#111C29', '#253C57', '#5C778F', '#5C778F', '#253C57', '#111B27']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        locations={[0.09, 0.19, 0.43, 0.58, 0.78, 0.92]}
        style={{ flex: 1 }}
      >
        {isLoading ? (
          <View style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent'
          }}>
            <ActivityIndicator size="large" color="#ffffff" />
            <Text style={{
              color: '#ffffff',
              fontSize: 16,
              marginTop: 16,
              textAlign: 'center',
              fontFamily: fontFamily?.font500 || 'System',
            }}>
              {loaderTitle}
            </Text>
          </View>
        ) : (
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
          <View style={{
            backgroundColor: '#0F0F0F',
            height: isDesktop ? 40 : 34,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: isDesktop ? 20 : 15,
            paddingRight: isDesktop ? 20 : 15,
          }}>
            <Text style={{
              color: '#979797',
              fontSize: isDesktop ? 18 : 16,
              fontFamily: fontFamily.font300
            }}>
              {reportName}
            </Text>
          </View>

          <View style={{
            marginTop: isDesktop ? 30 : 20,
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: isMobile ? 10 : 20
          }}>
            <View style={{
              width: isDesktop ? '70%' : '90%',
              maxWidth: isDesktop ? 600 : 400,
              alignItems: 'center',
              paddingHorizontal: isMobile ? 10 : 20
            }}>
              {Array.isArray(shopifyLocations) && shopifyLocations.length > 0 && (
                <Text style={{
                  fontSize: isDesktop ? 18 : isTablet ? 16 : 14,
                  color: '#ffffff',
                  fontFamily: fontFamily?.font600 || 'System',
                  marginBottom: isDesktop ? 50 : 40,
                  textAlign: 'center',
                }}>
                  Choose Location to Update
                </Text>
              )}

              {Array.isArray(shopifyLocations) && shopifyLocations.length > 0 ? (
                shopifyLocations.map((loc) => (
                  <TouchableOpacity
                    key={loc.id}
                    style={{
                      backgroundColor: selectedLocation?.id === loc.id ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                      borderWidth: selectedLocation?.id === loc.id ? 2 : 1,
                      borderColor: '#FFFFFF',
                      borderRadius: isDesktop ? 8 : 5,
                      alignItems: 'center',
                      width: '100%',
                      marginBottom: isDesktop ? 25 : 20,
                      paddingVertical: isDesktop ? 15 : isMobile ? 12 : 10,
                      paddingHorizontal: 10,
                    }}
                    onPress={() => handleLocationSelect(loc)}
                  >
                    <Text style={{
                      fontSize: isDesktop ? 16 : isTablet ? 16 : 15,
                      color: selectedLocation?.id === loc.id ? '#ffffff' : '#FFFFFF',
                      fontFamily: selectedLocation?.id === loc.id ? fontFamily?.font600 || 'System' : fontFamily?.font300 || 'System',
                      textAlign: 'center',
                      flexWrap: 'wrap',
                    }}>
                      {loc.store_name}- {loc.name}
                    </Text>
                  </TouchableOpacity>
                ))
              ) : (
                <View style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingVertical: isDesktop ? 30 : 20
                }}>
                  <Text style={{
                    fontSize: isDesktop ? 18 : 16,
                    color: '#FFFFFF',
                    fontFamily: fontFamily?.font400 || 'System',
                  }}>
                    No locations available
                  </Text>
                </View>
              )}

              <View style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                paddingVertical: isDesktop ? 30 : 20,
                paddingHorizontal: isMobile ? 5 : 10
              }}>
                <Text style={{
                  fontSize: isDesktop ? 16 : isTablet ? 15 : 14,
                  color: 'rgba(255, 255, 255, 0.8)',
                  textAlign: 'center',
                  lineHeight: isDesktop ? 24 : 20,
                  marginBottom: isDesktop ? 50 : 40,
                  fontFamily: fontFamily?.font300 || 'System',
                }}>
                  The locations for the connected Shopify stores are listed above. Select the location that inventory should be updated for.
                </Text>
              </View>
            </View>
          </View>

          <View style={{
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal: isMobile ? 10 : 20,
            marginTop: isDesktop ? 20 : 10
          }}>
            <View style={{
              width: isDesktop ? '70%' : '90%',
              maxWidth: isDesktop ? 600 : 400,
              alignItems: 'center',
              paddingHorizontal: isMobile ? 10 : 20
            }}>
              <Text style={{
                fontSize: isDesktop ? 18 : isTablet ? 16 : 14,
                color: '#ffffff',
                fontFamily: fontFamily?.font600 || 'System',
                marginBottom: isDesktop ? 50 : 40,
                textAlign: 'center',
              }}>
                Choose Quantity to update
              </Text>

              <View style={{
                flexDirection: isDesktop ? 'row' : 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isDesktop ? 20 : 0,
                width: '100%',
              }}>
                {['incoming', 'available'].map((option) => (
                  (reportMode === 'receive' || option !== 'incoming') && (
                    <TouchableOpacity
                      key={option}
                      style={{
                        backgroundColor: selectedOption === option ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        borderWidth: selectedOption === option ? 2 : 1,
                        borderColor: '#FFFFFF',
                        borderRadius: isDesktop ? 8 : 5,
                        alignItems: 'center',
                        width: 100,
                        marginBottom: isDesktop ? 0 : 20,
                        paddingVertical: 10,
                        paddingHorizontal: 10,
                      }}
                      onPress={() => handleOptionSelect(option)}
                    >
                      <Text style={{
                        fontSize: 16,
                        color: '#FFFFFF',
                        fontFamily: selectedOption === option ? fontFamily?.font600 || 'System' : fontFamily?.font300 || 'System',
                        textAlign: 'center',
                      }}>
                        {option === 'incoming' ? 'Incoming' : 'Available'}
                      </Text>
                    </TouchableOpacity>
                  )
                ))}
              </View>

              <Text style={{
                fontSize: isDesktop ? 16 : isTablet ? 15 : 14,
                color: 'rgba(255, 255, 255, 0.8)',
                textAlign: 'center',
                lineHeight: isDesktop ? 24 : 20,
                marginBottom: isDesktop ? 50 : 40,
                marginTop: isDesktop ? 30 : 20,
                fontFamily: fontFamily?.font300 || 'System',
                paddingHorizontal: isMobile ? 5 : 0,
              }}>
                If the inventory being received is currently tracked in Shopify as Incoming inventory you'll want to select this option. Otherwise it's best to update the available count.
              </Text>

              <TouchableOpacity
                style={{
                  backgroundColor: (!selectedOption || (Array.isArray(shopifyLocations) && shopifyLocations.length > 0 && !selectedLocation) || !pushPermissions.can_push) ? 'rgba(76, 175, 80, 0.3)' : '#4CAF50',
                  borderRadius: isDesktop ? 15 : 12,
                  paddingVertical: isDesktop ? 18 : 15,
                  paddingHorizontal: isDesktop ? 40 : 30,
                  marginBottom: isDesktop ? 30 : 20,
                  minWidth: isDesktop ? 250 : isMobile ? 180 : 200,
                  alignItems: 'center',
                }}
                onPress={() => {
                  console.log('🔍 Debug - Button pressed!');
                  handleReviewAndPush();
                }}
                disabled={!selectedOption || (Array.isArray(shopifyLocations) && shopifyLocations.length > 0 && !selectedLocation) || !pushPermissions.can_push}
              >
                <Text style={{
                  fontSize: isDesktop ? 18 : 16,
                  color: (!selectedOption || (Array.isArray(shopifyLocations) && shopifyLocations.length > 0 && !selectedLocation) || !pushPermissions.can_push) ? 'rgba(255, 255, 255, 0.5)' : '#ffffff',
                  fontFamily: fontFamily?.font600 || 'System',
                }}>
                  {!pushPermissions.can_push ? 'Push Disabled' : 'Review Changes & Push'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
        )}

        <Modal
          visible={showConfirmationModal}
          transparent={true}
          animationType="fade"
          onRequestClose={handleConfirmationCancel}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
          }}>
            <View style={{
              backgroundColor: '#1A1A1A',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 400,
              borderWidth: 1,
              borderColor: '#333',
            }}>
              <Text style={{
                color: '#FFFFFF',
                fontSize: 18,
                fontFamily: fontFamily?.font600 || 'System',
                marginBottom: 16,
                textAlign: 'center',
              }}>
                Location Confirmation
              </Text>

              <Text style={{
                color: '#CCCCCC',
                fontSize: 16,
                fontFamily: fontFamily?.font400 || 'System',
                marginBottom: 20,
                textAlign: 'center',
                lineHeight: 22,
              }}>
                Some products have no inventory at the selected location. Continue to update inventory for their default locations?
              </Text>

              {productsWithoutLocation.length > 0 && (
                <View style={{ marginBottom: 20 }}>
                  <Text style={{
                    color: '#999999',
                    fontSize: 14,
                    fontFamily: fontFamily?.font500 || 'System',
                    marginBottom: 8,
                  }}>
                    Products without location inventory:
                  </Text>
                  {productsWithoutLocation.map((product, index) => (
                    <Text key={index} style={{
                      color: '#CCCCCC',
                      fontSize: 14,
                      fontFamily: fontFamily?.font400 || 'System',
                      marginLeft: 8,
                      marginBottom: 4,
                    }}>
                      • {product.product_name} (Store: {product.store_name})
                    </Text>
                  ))}
                </View>
              )}

              <View style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 12,
              }}>
                <TouchableOpacity
                  onPress={handleConfirmationCancel}
                  style={{
                    flex: 1,
                    backgroundColor: '#333333',
                    paddingVertical: 12,
                    paddingHorizontal: 20,
                    borderRadius: 8,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: '#FFFFFF',
                    fontSize: 16,
                    fontFamily: fontFamily?.font500 || 'System',
                  }}>
                    Cancel
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handleConfirmationContinue}
                  style={{
                    flex: 1,
                    backgroundColor: '#007AFF',
                    paddingVertical: 12,
                    paddingHorizontal: 20,
                    borderRadius: 8,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: '#FFFFFF',
                    fontSize: 16,
                    fontFamily: fontFamily?.font600 || 'System',
                  }}>
                    Continue
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </LinearGradient>
  );
};
export default pushInventory;
